import {Nav, Navbar} from "react-bootstrap";
import {Envelope, Shield, BoxArrowRight, PencilSquare, CreditCard, LayoutSidebar, LayoutSidebarReverse, Images, List, X} from "react-bootstrap-icons";
import {useNavigate, useLocation} from 'react-router-dom';
import {AuthContext} from "../contexts/AuthContext";
import { AppStateContext } from "../contexts/AppStateContext";
import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useTheme} from "../contexts/ThemeContext";
import AccountDropdown from "./shared/AccountDropdown";
import { MenuCollapsibleSection } from "./shared/MenuCollapsibleSection";
import ChartLayoutSelector from "./StockChart/ChartLayoutSelector";
import SavedChartLayoutsMenu from "./StockChart/SavedChartLayoutsMenu";
import UndoButton from "./StockChart/UndoButton";
import JoinBetaButton from "./shared/JoinBetaButton";
import AddAlertButton from "./StockChart/AddAlertButton";
import { hasTradePlatformAccess } from "../utils/authAccess";
import TradeJournalLockoutActions, { useNarrowTradeHeader } from "./TradeJournalLockoutActions";
import StreakPill from "./TradingRules/StreakPill";
import { getAppNavRoutes } from "../utils/appNavRoutes";
import { DASHBOARD_ROUTES, dashboardResearchPath } from "../constants/dashboardRoutes";
import { chartEdgeControlRight, getChartSectionRight } from "../utils/chartSectionInset";

// Keep header controls visually ungrouped (no card wraps).

const tradeHeaderMenuBtnStyle = (colors) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 6,
  color: colors.text,
  cursor: 'pointer',
  transition: 'opacity 0.15s ease',
  outline: 'none',
  flexShrink: 0,
  position: 'relative',
});

const headerButtonStyle = (colors, disabled = false) => ({
  backgroundColor: colors.bgSecondary,
  border: `1px solid ${colors.border}`,
  color: colors.text,
  padding: '6px 12px',
  fontSize: 14,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  borderRadius: 6,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'color 0.2s ease',
  outline: 'none',
  boxShadow: 'none',
});

const CompanyTickerSearch = ({ colors, navigate, search }) => {
  const currentSymbol = (new URLSearchParams(search).get('symbol') || '').trim().toUpperCase();
  const [val, setVal] = React.useState(currentSymbol);
  React.useEffect(() => {
    setVal(currentSymbol);
  }, [currentSymbol]);
  const handleSubmit = (e) => {
    e.preventDefault();
    const sym = val.trim().toUpperCase();
    if (sym) navigate(dashboardResearchPath(sym));
  };
  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'center', gap: 5, flex: '1 1 auto', minWidth: 0 }}>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value.toUpperCase())}
        placeholder="Ticker"
        style={{
          background: colors.bgCard || colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: 7,
          color: colors.text,
          padding: '5px 10px',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.06em',
          outline: 'none',
          width: 90,
          minWidth: 0,
          flexShrink: 1,
        }}
      />
      <button
        type="submit"
        style={{
          background: colors.primary,
          border: 'none',
          borderRadius: 7,
          color: '#fff',
          padding: '5px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Go
      </button>
    </form>
  );
};

function TradeHeaderAiButton({ colors, isLight }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener('precisionAI:open', onOpen);
    window.addEventListener('precisionAI:close', onClose);
    return () => {
      window.removeEventListener('precisionAI:open', onOpen);
      window.removeEventListener('precisionAI:close', onClose);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={() => {
        window.dispatchEvent(new CustomEvent(open ? 'precisionAI:close' : 'precisionAI:open'));
      }}
      title={open ? 'Close PrecisionAI' : 'Open PrecisionAI'}
      aria-label={open ? 'Close PrecisionAI' : 'Open PrecisionAI'}
      aria-pressed={open}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 36,
        minWidth: 36,
        padding: '0 12px',
        borderRadius: 18,
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        border: `1px solid ${open ? colors.primary : (isLight ? colors.border : 'transparent')}`,
        cursor: 'pointer',
        flexShrink: 0,
        outline: 'none',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: '#111111', letterSpacing: '0.02em' }}>AI</span>
    </button>
  );
}

export const Header = ({ dashboardSidebarCollapsed = true } = {}) => {
  const { user, logout } = useContext(AuthContext);
  const { chartLayout, setChartLayout, charts, activeChartId } = useContext(AppStateContext);
  const { colors, themeName, isLight } = useTheme();
  const navigate = useNavigate();
  const navbarRef = useRef(null);
  const tradeHeaderLeadingRef = useRef(null);
  const tradeHeaderTrailingRef = useRef(null);
  const chartEdgeControlsRef = useRef(null);
  const [chartControlsRight, setChartControlsRight] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isNarrowTradeHeader = useNarrowTradeHeader();
  const isSuperuser = user?.superuser || false;
  const location = useLocation();
  const path = location.pathname;
  const isResearchPage = path === DASHBOARD_ROUTES.research;
  const isTradePage = path === '/trade' || path.startsWith('/trade/');
  const isRuleConfigurationPage = path === '/rule-configuration';
  const hasTradeAccess = hasTradePlatformAccess(user);
  const appNav = user ? getAppNavRoutes(user) : null;
  const hasPaidSubscription = user?.subscriptionStatus?.subscription && ['active', 'trialing'].includes(user?.subscriptionStatus?.subscription?.status);

  // Handle clicks outside menu dropdown to collapse it (non-trade pages only; trade uses DashboardSidebar)
  useEffect(() => {
    if (isTradePage) return undefined;

    const handleClickOutside = (event) => {
      if (isMobileMenuOpen) {
        const menuElement = navbarRef.current?.querySelector('.navbar-collapse');
        const toggleButton = navbarRef.current?.querySelector('.trade-header-menu-btn, .navbar-toggler');
        const navbarElement = navbarRef.current;
        
        // Don't close if clicking the toggle button (let onToggle handle it)
        if (toggleButton && toggleButton.contains(event.target)) {
          return;
        }
        
        const clickedElement = event.target;
        
        // Check if click is inside the navbar element (which contains both menu and toggle)
        const isInNavbar = navbarElement && navbarElement.contains(clickedElement);
        
        // Check if click is inside the menu element itself
        const isInMenu = menuElement && menuElement.contains(clickedElement);
        
        // Check if click is in any Bootstrap dropdown menu (these can render outside the menu container)
        const dropdownMenu = clickedElement.closest('.dropdown-menu');
        
        // Check if the dropdown menu belongs to a dropdown toggle that's inside our menu
        let isInMenuDropdown = false;
        if (dropdownMenu) {
          // Find the dropdown container that owns this menu
          const dropdownContainer = dropdownMenu.closest('.dropdown');
          if (dropdownContainer && menuElement?.contains(dropdownContainer)) {
            isInMenuDropdown = true;
          }
        }
        
        // Also check if clicking on a dropdown toggle button inside the menu
        const dropdownToggle = clickedElement.closest('[data-bs-toggle="dropdown"], .dropdown-toggle');
        const isMenuDropdownToggle = dropdownToggle && menuElement?.contains(dropdownToggle);
        
        // Check if click is within chart controls section INSIDE THE MENU (not chart overlays outside)
        // Only check for chart controls that are actually inside the navbar-collapse menu
        const chartControlsSection = menuElement?.querySelector('.header-menu-layout-section');
        const isInMenuChartControls = chartControlsSection && chartControlsSection.contains(clickedElement);
        
        // Don't close if clicking inside the navbar/menu, any dropdowns opened from the menu, or chart controls INSIDE the menu
        if (isInNavbar || isInMenu || isInMenuDropdown || isMenuDropdownToggle || isInMenuChartControls) {
          return;
        }
        
        // Close if clicking outside the navbar entirely
        setIsMobileMenuOpen(false);
      }
    };

    // Use capture phase to catch events before they're stopped by nested handlers
    // Capture phase runs BEFORE target phase, so we catch it before stopPropagation
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isMobileMenuOpen, isTradePage]);

  useLayoutEffect(() => {
    if (!isTradePage) {
      setChartControlsRight(null);
      return undefined;
    }
    const measure = () => {
      const navbarRect = navbarRef.current?.getBoundingClientRect();
      if (!navbarRect) return;
      const next = chartEdgeControlRight({
        navbarRight: navbarRect.right,
        chartRight: getChartSectionRight(),
        trailingLeft: tradeHeaderTrailingRef.current?.getBoundingClientRect().left,
        controlsWidth: chartEdgeControlsRef.current?.offsetWidth,
        leftGroupRight: tradeHeaderLeadingRef.current?.getBoundingClientRect().right,
      });
      setChartControlsRight((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('chart-section-geometry', measure);
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (tradeHeaderTrailingRef.current) observer?.observe(tradeHeaderTrailingRef.current);
    if (tradeHeaderLeadingRef.current) observer?.observe(tradeHeaderLeadingRef.current);
    if (chartEdgeControlsRef.current) observer?.observe(chartEdgeControlsRef.current);
    return () => {
      window.removeEventListener('chart-section-geometry', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [isTradePage]);

  // React-Bootstrap Navbar is controlled via expanded/onToggle

  return (
    <>
    <Navbar ref={navbarRef} variant="dark" expand={false} expanded={isTradePage ? false : isMobileMenuOpen} onToggle={isTradePage ? undefined : (next) => setIsMobileMenuOpen(next)} className={isTradePage ? 'd-none d-md-block navbar-trade-page' : ''} style={{ 
      backgroundColor: colors.bgHeader,
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${isTradePage ? colors.borderLight : colors.border}`,
      zIndex: 1000,
      position: 'relative',
    }}>
      <div className="container-fluid" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        flexWrap: 'nowrap', 
        alignItems: 'center',
        width: '100%',
        padding: isTradePage ? '0 8px 0 12px' : 0,
        gap: isTradePage ? 6 : undefined,
      }}>
        <Navbar.Brand 
          onClick={() => navigate('/home')} 
          style={{
            paddingLeft: isTradePage ? 4 : 15,
            cursor: 'pointer',
            flex: '0 0 auto',
            flexShrink: 0,
            marginLeft: isTradePage ? 0 : undefined,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <img 
              src={`${process.env.PUBLIC_URL}/favicon.png`} 
              alt=""
              role="presentation"
              style={{ 
                height: isTradePage ? '30px' : '32px',
                width: 'auto',
                objectFit: 'contain',
                borderRadius: 6
              }} 
            />
            <span 
              key={themeName}
              className="d-none d-md-inline" 
              style={{ 
                fontWeight: 600, 
                fontSize: isTradePage ? 18 : 20, 
                letterSpacing: isTradePage ? 0.5 : 1,
                background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >PrecisionTrader</span>
            {/* Account Dropdown - Desktop, aligned left next to PrecisionTrader (non-trade pages) */}
            {/* AccountDropdown moved to sidebar */}
          </div>
        </Navbar.Brand>
        
        {/* Account Dropdown - Only show when logged in AND has trade access */}
        {user && hasTradeAccess && (
          <>
            {/* Mobile: account (short label) + optional journal — flex middle so hamburger stays on-screen */}
            <div
              className="d-md-none d-flex align-items-center"
              style={{ flex: '1 1 auto', minWidth: 0, gap: 6, paddingLeft: 8 }}
            >
              {isResearchPage ? (
                <CompanyTickerSearch colors={colors} navigate={navigate} search={location.search} />
              ) : isTradePage ? (
                <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: 160 }}>
                  <AccountDropdown compact={true} labelTailChars={5} />
                </div>
              ) : null}
              {isTradePage && (
                <button
                  aria-label="New Trade Journal"
                  title="New Trade Journal"
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('tradeJournal:open'))}
                  style={{ ...headerButtonStyle(colors), flexShrink: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
                >
                  <PencilSquare size={16} />
                </button>
              )}
            </div>

            {/* Chart controls + account (desktop trade) */}
            <div className="d-none d-md-flex header-desktop-account" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: '12px', flex: 1, minWidth: 0 }}>
              {isResearchPage && (
                <CompanyTickerSearch colors={colors} navigate={navigate} search={location.search} />
              )}
              {isTradePage && (
                <div ref={tradeHeaderLeadingRef} style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, minWidth: 0 }}>
                  <AccountDropdown dense sizeToContent />
                  {(() => {
                    const activeChart = charts?.find(c => c.id === activeChartId);
                    const activeChartTicker = activeChart?.ticker;
                    return activeChartTicker ? (
                      <AddAlertButton ticker={activeChartTicker} isMobile={false} variant="headerIcon" />
                    ) : null;
                  })()}
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('leftSidebar:toggle'))}
                    style={tradeHeaderMenuBtnStyle(colors)}
                    title="Toggle left sidebar"
                    aria-label="Toggle left sidebar"
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <LayoutSidebar size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('rightSidebar:toggle'))}
                    style={tradeHeaderMenuBtnStyle(colors)}
                    title="Toggle right sidebar"
                    aria-label="Toggle right sidebar"
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <LayoutSidebarReverse size={20} />
                  </button>
                </div>
              )}
            </div>

            {isTradePage && (
              <div
                ref={chartEdgeControlsRef}
                className="trade-header-chart-edge-controls d-none d-md-flex"
                style={{
                  position: 'absolute',
                  right: chartControlsRight == null ? 16 : chartControlsRight,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  alignItems: 'center',
                  gap: 10,
                  zIndex: 2,
                  visibility: chartControlsRight == null ? 'hidden' : 'visible',
                }}
              >
                <ChartLayoutSelector
                  currentLayout={chartLayout}
                  onLayoutChange={setChartLayout}
                  compact
                  variant="headerIcon"
                />
                <SavedChartLayoutsMenu variant="headerIcon" />
              </div>
            )}

            {isTradePage && (
              <div ref={tradeHeaderTrailingRef} className="d-none d-md-flex align-items-center" style={{ flexDirection: 'row', marginLeft: 'auto', gap: 10 }}>
                {/* Order: Streak, AI, Journal, Trading Rules, App nav sidebar */}
                <StreakPill compact />
                <TradeHeaderAiButton colors={colors} isLight={isLight} />
                {!isNarrowTradeHeader && <TradeJournalLockoutActions variant="header" />}
                <button
                  type="button"
                  className="trade-header-menu-btn"
                  onClick={() => window.dispatchEvent(new Event('dashboardSidebar:toggle'))}
                  style={tradeHeaderMenuBtnStyle(colors)}
                  title={dashboardSidebarCollapsed ? 'Open sidebar' : 'Close sidebar'}
                  aria-label={dashboardSidebarCollapsed ? 'Open sidebar' : 'Close sidebar'}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  {dashboardSidebarCollapsed ? <List size={20} /> : <X size={20} />}
                </button>
              </div>
            )}
          </>
        )}

        {/* Early Access Button - Desktop */}
        <JoinBetaButton variant="header-desktop" />
        
        {/* Early Access Button - Mobile */}
        <JoinBetaButton variant="header-mobile" />

        {user && !isTradePage && appNav?.showTrade && (
          <a
            href={appNav.tradePath}
            className="d-none d-md-inline"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey) return;
              e.preventDefault();
              navigate(appNav.tradePath);
            }}
            style={{
              color: colors.text,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
              opacity: 0.9,
              marginLeft: 'auto',
              marginRight: 8,
              whiteSpace: 'nowrap',
              padding: '6px 8px',
              flex: '0 0 auto',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.9'; }}
          >
            Trade
          </a>
        )}

        {!isTradePage && (
          <Navbar.Toggle 
            aria-controls="basic-navbar-nav" 
            style={{ 
              marginLeft: 4, 
              marginRight: 6,
              borderColor: colors.border,
              color: colors.text,
              flex: '0 0 auto',
              flexShrink: 0,
            }}
          >
            <span style={{ 
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='${encodeURIComponent(colors.text)}' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e")`,
              display: 'inline-block',
              width: '1.5em',
              height: '1.5em',
              verticalAlign: 'middle',
              content: '',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '100%'
            }} />
          </Navbar.Toggle>
        )}
      </div>
      
      {!isTradePage && (
      <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
        <Nav className="text-end" style={{ 
          alignItems: 'stretch', 
          width: '100%',
          flexDirection: 'column',
          padding: 0
        }}>
          {!user ? (
            <>
              <Nav.Link 
                onClick={() => navigate('/login')}
                style={{ 
                  color: 'var(--dark-text)', 
                  cursor: 'pointer', 
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  transition: 'color 0.2s ease',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  borderRadius: 0
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = 'var(--dark-text)'}
              >
                Login
              </Nav.Link>
              <Nav.Link 
                onClick={() => navigate('/register')}
                style={{ 
                  color: 'var(--dark-text)', 
                  cursor: 'pointer', 
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  transition: 'color 0.2s ease',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  borderRadius: 0
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = 'var(--dark-text)'}
              >
                Register
              </Nav.Link>
              <Nav.Link 
                onClick={() => navigate('/contact')}
                style={{ 
                  color: 'var(--dark-text)', 
                  cursor: 'pointer', 
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  transition: 'color 0.2s ease',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  borderRadius: 0
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = 'var(--dark-text)'}
              >
                Contact Us
              </Nav.Link>
            </>
          ) : (
            <>
              <div
                style={{
                  maxHeight: 'min(85dvh, calc(100dvh - 72px))',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  width: '100%',
                  alignSelf: 'flex-end',
                }}
              >
              <div className="header-menu-panel" style={{ alignSelf: 'flex-end', display: 'inline-flex', flexDirection: 'column', backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderLight}`, borderRadius: 8, width: '100%' }}>
                  {/* User email */}
                  <div style={{ padding: '10px 12px', color: colors.textLight, borderBottom: `1px solid ${colors.borderLight}`, fontWeight: 600 }}>
                    {user?.email || 'Account'}
                  </div>

                  {user && appNav && (
                    <MenuCollapsibleSection title="Navigate" defaultOpen isFirst colors={colors}>
                      {appNav.showDashboard && (
                        <a
                          href={appNav.dashboardPath}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey) return;
                            e.preventDefault();
                            navigate(appNav.dashboardPath);
                            setIsMobileMenuOpen(false);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                        >
                          <span>Dashboard</span>
                        </a>
                      )}
                      <a
                        href={appNav.tradePath}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(appNav.tradePath);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Trade</span>
                      </a>
                      <a
                        href={appNav.homePath}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(appNav.homePath);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Home</span>
                      </a>
                      <a
                        href={appNav.featuresPath}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(appNav.featuresPath);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Features</span>
                      </a>
                    </MenuCollapsibleSection>
                  )}

                  {user && !isTradePage && (
                    <MenuCollapsibleSection title="Insights" defaultOpen colors={colors}>
                      <a
                        href={DASHBOARD_ROUTES.research}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(DASHBOARD_ROUTES.research);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Research</span>
                      </a>
                      <a
                        href={DASHBOARD_ROUTES.health}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(DASHBOARD_ROUTES.health);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Account Health</span>
                      </a>
                      <a
                        href={DASHBOARD_ROUTES.performance}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(DASHBOARD_ROUTES.performance);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Account Performance</span>
                      </a>
                      <a
                        href="/dashboard"
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate('/dashboard');
                          setIsMobileMenuOpen(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                      >
                        <span>Account Health</span>
                      </a>
                    </MenuCollapsibleSection>
                  )}

                  <MenuCollapsibleSection title="Settings" defaultOpen colors={colors}>
                  {isTradePage && (
                    <button
                      onClick={() => {
                        window.dispatchEvent(new Event('chartSettings:open'));
                        setIsMobileMenuOpen(false);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px', cursor: 'pointer' }}
                    >
                      <span>Chart Settings</span>
                    </button>
                  )}
                  <a
                    href="/dashboard/trading-rules"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) return;
                      e.preventDefault();
                      navigate('/dashboard/trading-rules');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <span>Trading Rules</span>
                  </a>
                  <a
                    href={appNav?.settingsPath || '/settings'}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) return;
                      e.preventDefault();
                      navigate(appNav?.settingsPath || '/settings');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <span>Settings</span>
                  </a>
                  </MenuCollapsibleSection>

                  {isTradePage && (
                    <div className="d-md-none">
                    <MenuCollapsibleSection title="Layout" defaultOpen colors={colors}>
                      <div className="header-menu-layout-section">
                      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', justifyContent: 'flex-start', width: '100%' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ChartLayoutSelector 
                              currentLayout={chartLayout} 
                              onLayoutChange={(layout) => {
                                setChartLayout(layout);
                                setIsMobileMenuOpen(false);
                              }}
                              showLabel={true}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <LayoutSidebar
                              size={18}
                              color={colors.text}
                              title="Toggle left sidebar (watchlists)"
                              onClick={() => {
                                window.dispatchEvent(new Event('leftSidebar:toggle'));
                                setIsMobileMenuOpen(false);
                              }}
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              role="button"
                              aria-label="Toggle left sidebar"
                            />
                            <LayoutSidebarReverse
                              size={18}
                              color={colors.text}
                              title="Toggle right sidebar (order panel)"
                              onClick={() => {
                                window.dispatchEvent(new Event('rightSidebar:toggle'));
                                setIsMobileMenuOpen(false);
                              }}
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              role="button"
                              aria-label="Toggle right sidebar"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-start' }}>
                          <UndoButton />
                        </div>
                      </div>
                      </div>
                    </MenuCollapsibleSection>
                    </div>
                  )}

                  <MenuCollapsibleSection title="Learning" defaultOpen={false} colors={colors}>
                  <a
                    href="/learn#quick-start"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        return;
                      }
                      e.preventDefault();
                      navigate('/learn#quick-start');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <span>Quick Start</span>
                  </a>
                  <a
                    href="/learn#trading-fundamentals"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        return;
                      }
                      e.preventDefault();
                      navigate('/learn#trading-fundamentals');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <span>Trading Fundamentals</span>
                  </a>
                  <a
                    href="/learn#software-guides"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        return;
                      }
                      e.preventDefault();
                      navigate('/learn#software-guides');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <span>Software Guides</span>
                  </a>
                  </MenuCollapsibleSection>

                  <MenuCollapsibleSection title="Account" defaultOpen colors={colors}>
                  {!hasPaidSubscription && (
                    <button
                      onClick={() => { navigate('/pricing'); setIsMobileMenuOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                    >
                      <CreditCard size={14} />
                      <span>Pricing</span>
                    </button>
                  )}
                  {isSuperuser && (
                    <button
                      onClick={() => { navigate('/content-creator'); setIsMobileMenuOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: '#d4a017', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                    >
                      <Images size={14} />
                      <span>Content Creator</span>
                    </button>
                  )}
                  {isSuperuser && (
                    <button
                      onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: '#ef4444', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                    >
                      <Shield size={14} />
                      <span>Admin Area</span>
                    </button>
                  )}
                  <button
                    onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <Envelope size={14} />
                    <span>Contact Us</span>
                  </button>
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--dark-text)', padding: '8px 12px 8px 24px', width: '100%', fontSize: '14px' }}
                  >
                    <BoxArrowRight size={14} />
                    <span>Logout</span>
                  </button>
                  </MenuCollapsibleSection>
              </div>
              </div>
            </>
          )}
        </Nav>
      </Navbar.Collapse>
      )}
    </Navbar>
    </>
  )
}